// frontend/src/pages/admin/reports/TopCustomers.js
import React, { useState, useEffect } from 'react';
import axios from '../../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiUsers,
  FiDollarSign,
  FiShoppingCart,
  FiDownload,
  FiMail,
  FiPhone
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import DateRangePicker from '../../../components/DateRangePicker';

const TopCustomers = () => {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);

  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // ✅ FIX: Only fetch when dateRange changes
  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/reports/customers/top', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end,
          limit: 10
        }
      });

      if (response.data.success) {
        setCustomers(response.data.data.customers);
      }
    } catch (error) {
      console.error('Fetch top customers error:', error);
      toast.error('Топ харилцагчийн тайлан татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (start, end) => {
    setDateRange({ start, end });
  };

  const exportToExcel = () => {
    toast.info('Excel export функц удахгүй нэмэгдэнэ');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner-lg"></div>
      </div>
    );
  }

  const totalSpent = customers.reduce((sum, c) => sum + (parseFloat(c.total_spent) || 0), 0);
  const totalOrders = customers.reduce((sum, c) => sum + (parseInt(c.order_count) || 0), 0);

  return (
    <div>
      {/* Date Range Picker */}
      <DateRangePicker onDateChange={handleDateChange} />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Total Customers */}
        <div className="card bg-gradient-to-br from-orange-50 to-orange-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-semibold">Топ харилцагчид</p>
              <p className="text-3xl font-bold text-orange-900">
                {customers.length}
              </p>
            </div>
            <FiUsers className="text-orange-600" size={48} />
          </div>
        </div>

        {/* Total Spent */}
        <div className="card bg-gradient-to-br from-green-50 to-green-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-semibold">Нийт худалдан авалт</p>
              <p className="text-3xl font-bold text-green-900">
                {totalSpent.toLocaleString()}₮
              </p>
            </div>
            <FiDollarSign className="text-green-600" size={48} />
          </div>
        </div>

        {/* Total Orders */}
        <div className="card bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-semibold">Нийт захиалга</p>
              <p className="text-3xl font-bold text-blue-900">
                {totalOrders}
              </p>
            </div>
            <FiShoppingCart className="text-blue-600" size={48} />
          </div>
        </div>
      </div>

      {/* Top Customers Chart */}
      {customers.length > 0 && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              🏆 Топ 10 харилцагчид
            </h3>
            <button onClick={exportToExcel} className="btn btn-secondary">
              <FiDownload className="mr-2" />
              Excel татах
            </button>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={customers} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="full_name" type="category" width={120} />
              <Tooltip formatter={(value) => value.toLocaleString() + '₮'} />
              <Bar dataKey="total_spent" fill="#f97316" name="Нийт худалдан авалт" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Customers Table */}
      <div className="card">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          📋 Дэлгэрэнгүй
        </h3>
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-th">№</th>
                <th className="table-th">Харилцагч</th>
                <th className="table-th">Холбоо барих</th>
                <th className="table-th text-right">Захиалга</th>
                <th className="table-th text-right">Нийт дүн</th>
                <th className="table-th text-right">Дундаж</th>
                <th className="table-th">Сүүлийн худалдан авалт</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-500">
                    <FiUsers size={48} className="mx-auto mb-3 opacity-50" />
                    <p>Харилцагч олдсонгүй</p>
                  </td>
                </tr>
              ) : (
                customers.map((customer, index) => (
                  <tr key={customer.id} className="table-row">
                    <td className="table-td">
                      <span className="font-bold text-gray-600">{index + 1}</span>
                    </td>
                    <td className="table-td">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                          <FiUsers className="text-orange-600" size={20} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{customer.full_name}</p>
                          <p className="text-xs text-gray-500">ID: {customer.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-td">
                      <div className="space-y-1">
                        {customer.phone && (
                          <p className="text-sm flex items-center text-gray-600">
                            <FiPhone className="mr-1" size={12} />
                            {customer.phone}
                          </p>
                        )}
                        {customer.email && (
                          <p className="text-sm flex items-center text-gray-600">
                            <FiMail className="mr-1" size={12} />
                            {customer.email}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="table-td text-right">
                      <span className="badge badge-blue">
                        {customer.order_count} удаа
                      </span>
                    </td>
                    <td className="table-td text-right">
                      <p className="font-bold text-green-600">
                        {parseFloat(customer.total_spent).toLocaleString()}₮
                      </p>
                    </td>
                    <td className="table-td text-right text-sm text-gray-600">
                      {parseFloat(customer.average_order_value).toLocaleString()}₮
                    </td>
                    <td className="table-td text-sm text-gray-600">
                      {new Date(customer.last_purchase_date).toLocaleDateString('mn-MN')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TopCustomers;